import { Fragment, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, FileText, History, Pencil } from 'lucide-react';
import type { PolicyDto } from '@workforce/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fileUrl } from '@/lib/leave';
import { useTz } from '@/lib/timezone';
import { t } from '@/lib/i18n';
import { FileUpload, type UploadedFile } from '@/components/FileUpload';
import { Button, Card, EmptyState, ErrorBanner, Field, Input, Modal, PageHeader, Spinner, Textarea, Toggle } from '@/components/ui';

/**
 * Plain text → headings, bullet lists and paragraphs. Rendered as React text
 * (never as HTML), so nothing typed here can run in the browser.
 */
export function PolicyText({ body }: { body: string }) {
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) blocks.push(<p key={blocks.length} dir="auto" className="leading-7 text-slate-700">{para.join(' ')}</p>);
    if (bullets.length)
      blocks.push(
        <ul key={blocks.length} dir="auto" className="list-disc space-y-1 ps-6 text-slate-700">
          {bullets.map((b, i) => (
            <li key={i} dir="auto">{b}</li>
          ))}
        </ul>,
      );
    para = [];
    bullets = [];
  };
  for (const raw of body.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) {
      flush();
    } else if (/^#{1,3}\s/.test(line)) {
      flush();
      const level = line.match(/^#+/)![0].length;
      const text = line.replace(/^#+\s*/, '');
      blocks.push(
        level === 1 ? (
          <h2 key={blocks.length} dir="auto" className="pt-2 text-lg font-semibold text-slate-900">{text}</h2>
        ) : (
          <h3 key={blocks.length} dir="auto" className="pt-1 font-semibold text-slate-900">{text}</h3>
        ),
      );
    } else if (/^[-*•]\s/.test(line)) {
      if (para.length) {
        const p = para;
        para = [];
        blocks.push(<p key={blocks.length} dir="auto" className="leading-7 text-slate-700">{p.join(' ')}</p>);
      }
      bullets.push(line.replace(/^[-*•]\s*/, ''));
    } else {
      if (bullets.length) flush();
      para.push(line);
    }
  }
  flush();
  return <div className="space-y-3">{blocks.map((b, i) => <Fragment key={i}>{b}</Fragment>)}</div>;
}

export function PolicyPage() {
  const { can } = useAuth();
  const { fmt } = useTz();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['policy'], queryFn: () => api.get<PolicyDto | null>('/policy') });
  const history = useQuery({ queryKey: ['policy', 'history'], queryFn: () => api.get<PolicyDto[]>('/policy/history'), enabled: can('policy.write') });
  const [edit, setEdit] = useState<{ title: string; body: string; file: UploadedFile | null; notify: boolean } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const save = useMutation({
    mutationFn: () => api.post<PolicyDto>('/policy', { title: edit!.title, body: edit!.body, fileId: edit!.file?.id ?? null, notify: edit!.notify }),
    onSuccess: () => {
      setEdit(null);
      void qc.invalidateQueries({ queryKey: ['policy'] });
    },
  });
  const p = q.data;

  return (
    <div>
      <PageHeader
        title={t('Internal Policy')}
        subtitle={p ? t('Version {n} · updated {date} by {name}', { n: p.version, date: fmt(p.publishedAt, null, 'dd LLL yyyy'), name: p.publishedBy }) : t('The company rules everyone works by.')}
        actions={
          can('policy.write') && (
            <div className="flex gap-2">
              {!!history.data?.length && (
                <Button variant="secondary" icon={<History className="h-4 w-4" />} onClick={() => setShowHistory(true)}>
                  {t('Earlier versions')}
                </Button>
              )}
              <Button
                icon={<Pencil className="h-4 w-4" />}
                onClick={() => {
                  save.reset();
                  setEdit({ title: p?.title ?? t('PCBS internal policy'), body: p?.body ?? '', file: p?.file ?? null, notify: true });
                }}
              >
                {p ? t('Edit policy') : t('Write the policy')}
              </Button>
            </div>
          )
        }
      />
      <ErrorBanner error={q.error} />
      {q.isLoading ? (
        <Spinner />
      ) : !p ? (
        <Card>
          <EmptyState
            icon={<BookOpen className="h-8 w-8" />}
            title={t('The internal policy has not been published yet')}
            description={can('policy.write') ? t('Write it here or attach the policy PDF. Everyone will be able to read it.') : t('It will appear here once the administration publishes it.')}
          />
        </Card>
      ) : (
        <Card>
          <article className="mx-auto max-w-3xl space-y-4">
            <h1 className="text-2xl font-semibold text-slate-900">{p.title}</h1>
            {p.file && (
              <a href={fileUrl(p.file.id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-brand-700 hover:bg-slate-50">
                <FileText className="h-4 w-4" /> {p.file.filename}
              </a>
            )}
            {p.body.trim() && <PolicyText body={p.body} />}
          </article>
        </Card>
      )}

      <Modal
        open={!!edit}
        onClose={() => setEdit(null)}
        title={p ? t('Edit policy') : t('Write the policy')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEdit(null)}>
              {t('Cancel')}
            </Button>
            <Button loading={save.isPending} disabled={!edit?.title.trim() || (!edit.body.trim() && !edit.file)} onClick={() => save.mutate()}>
              {t('Publish')}
            </Button>
          </>
        }
      >
        {edit && (
          <div className="space-y-4">
            <ErrorBanner error={save.error} />
            <Field label={t('Title')} required>
              <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} maxLength={150} />
            </Field>
            <Field label={t('Policy text')} hint={t('Leave an empty line between paragraphs. Start a line with "# " for a heading and "- " for a bullet point.')}>
              <Textarea rows={14} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} className="font-mono text-sm" />
            </Field>
            <Field label={t('Policy document (optional)')}>
              <FileUpload kind="policy" value={edit.file} onChange={(f) => setEdit({ ...edit, file: f })} label={t('Attach PDF')} />
            </Field>
            <Toggle checked={edit.notify} onChange={(v) => setEdit({ ...edit, notify: v })} label={t('Notify everyone')} description={t('Sends a notification that the policy was updated.')} />
            <p className="text-xs text-slate-500">{t('Every published version is kept.')}</p>
          </div>
        )}
      </Modal>

      <Modal open={showHistory} onClose={() => setShowHistory(false)} title={t('Earlier versions')} size="lg">
        <ul className="divide-y divide-slate-100">
          {(history.data ?? []).map((v) => (
            <li key={v.version} className="py-3">
              <p className="text-sm font-medium text-slate-900">
                {t('Version {n}', { n: v.version })} · {v.title}
              </p>
              <p className="text-xs text-slate-500">{t('{date} by {name}', { date: fmt(v.publishedAt, null, 'dd LLL yyyy HH:mm'), name: v.publishedBy })}</p>
              <details className="mt-1">
                <summary className="cursor-pointer text-xs text-brand-700">{t('Show text')}</summary>
                <div className="mt-2 rounded-lg bg-slate-50 p-3 text-sm">
                  <PolicyText body={v.body} />
                </div>
              </details>
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  );
}
